import React from 'react'
import { useParams } from 'react-router'
import { FavoriteChannelButton } from '@components/FavoriteChannelButton/FavoriteChannelButton'
import { Badge, Box, ButtonText, Icon, Stack } from '@ui'
import { useChannelEntitlements } from 'hooks/useChannelEntitlements'
import { PATHS } from 'routes'
import { addressFromSpaceId } from 'ui/utils/utils'
import { useMuteSettings, useSpaceUnreads } from 'use-towns-client'

import { NavItem } from './_NavItem'

type Props = {
    id: string
    spaceId: string
    channelLabel: string
    channelId: string
    favorite?: boolean
    mentionCount?: number
    isUnreadSection?: boolean
    dataTestId?: string
}

export const ChannelNavItem = React.memo(function ChannelNavItem(props: Props) {
    const { channelSlug } = useParams()

    const {
        id,
        spaceId,
        channelId,
        channelLabel,
        mentionCount = 0,
        favorite = false,
        isUnreadSection = false,
    } = props

    const { hasSomeEntitlement } = useChannelEntitlements({
        spaceId,
        channelId,
    })

    const link = `/${PATHS.SPACES}/${addressFromSpaceId(spaceId)}/channels/${channelId}/`
    const isHighlight = channelId === channelSlug
    const { channelIsMuted } = useMuteSettings({
        spaceId,
        channelId,
    })

    const isMuted = channelIsMuted

    const { spaceUnreadChannelIds } = useSpaceUnreads()
    const showUnread = spaceUnreadChannelIds[spaceId]?.has(channelId)

    return (
        <>
            <NavItem to={link} id={id} exact={false} paddingY="xxs" minHeight="x5">
                <Stack
                    horizontal
                    width="100%"
                    gap="sm"
                    alignItems="center"
                    data-testid={props.dataTestId}
                >
                    <Box>
                        {}
                        <Icon
                            type={hasSomeEntitlement ? 'lock' : 'tag'}
                            padding="line"
                            background="level2"
                            color={showUnread ? 'default' : 'gray2'}
                            size="square_lg"
                            shrink={false}
                        />
                    </Box>
                    <ButtonText
                        strong={showUnread}
                        color={showUnread ? 'default' : isHighlight ? 'default' : undefined}
                    >
                        {channelLabel}
                    </ButtonText>
                    <Box grow />
                    <FavoriteChannelButton
                        isUnreadSection={isUnreadSection}
                        channelId={channelId}
                        favorite={favorite}
                    />

                    <Stack horizontal gap="sm" alignItems="center">
                        {isMuted && <Icon size="square_xs" type="muteActive" color="gray2" />}
                        {!!mentionCount && <Badge value={mentionCount}>{mentionCount}</Badge>}
                    </Stack>
                </Stack>
            </NavItem>
        </>
    )
})
